import { useEffect } from 'react'
import { Controller, type FieldError, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@gto/ui/button'

import type { AdminAffiliateTierSchema, AffiliateAdminCreatePartnerSchema } from '@/api/gen'
import { AppDialog } from '@/components/app-dialog'
import { fieldErrorText, SelectField, TextField } from '@/components/form'
import { MutationError } from '@/components/mutation-error'
import {
  buildNewPartnerRequest,
  defaultTierId,
  MAX_DISPLAY_NAME_LENGTH,
  NEW_PARTNER_FORM_DEFAULTS,
  NEW_PARTNER_SERVER_FIELDS,
  newPartnerFormSchema,
  type NewPartnerFormValues,
} from '@/domain/new-partner'
import { formatRate } from '@/pages/admin/partners/partner-detail/cards/format-rate'
import {
  extractValidationDetail,
  extractValidationFieldErrors,
} from '@/utils/extract-validation-detail'

type NewPartnerDialogProps = {
  open: boolean
  tiers: readonly AdminAffiliateTierSchema[]
  submitting: boolean
  /** Latest create-partner mutation error. A 422 that names a field lands on that field. */
  serverError: unknown
  onClose: () => void
  onSubmit: (body: AffiliateAdminCreatePartnerSchema) => void
}

const isServerField = (field: string): field is keyof typeof NEW_PARTNER_SERVER_FIELDS =>
  Object.hasOwn(NEW_PARTNER_SERVER_FIELDS, field)

/**
 * Whether the error needs the dialog's banner. A 422 whose every entry names a
 * form field is already shown on those fields; anything else — an unknown
 * field, a service-level 422, a 404 for a deleted tier, a 500 — must reach the
 * banner so the admin is never left without feedback.
 */
const showsBanner = (serverError: unknown): boolean => {
  if (serverError == null) {
    return false
  }
  const fieldErrors = extractValidationFieldErrors(serverError)

  return fieldErrors === null || fieldErrors.some((entry) => !isServerField(entry.field))
}

export const NewPartnerDialog = (props: NewPartnerDialogProps) => {
  const { formatMessage } = useIntl()

  return (
    <AppDialog
      open={props.open}
      width={520}
      onClose={props.onClose}
    >
      <AppDialog.Header
        subtitle={formatMessage({ id: 'newPartner.subtitle' })}
        title={formatMessage({ id: 'newPartner.title' })}
      />
      {/* Mounted only while open, so every opening starts from a fresh form. The key
          remounts it when the tier list arrives, so the entry tier is pre-selected
          even when the dialog opened before the list loaded. */}
      {props.open && (
        <NewPartnerForm
          key={props.tiers.length > 0 ? 'tiers-loaded' : 'tiers-pending'}
          {...props}
        />
      )}
    </AppDialog>
  )
}

const NewPartnerForm = ({
  tiers,
  submitting,
  serverError,
  onClose,
  onSubmit,
}: NewPartnerDialogProps) => {
  const { formatMessage } = useIntl()
  const form = useForm<NewPartnerFormValues>({
    resolver: zodResolver(newPartnerFormSchema),
    defaultValues: { ...NEW_PARTNER_FORM_DEFAULTS, tierId: defaultTierId(tiers) },
  })
  const errorText = (error: FieldError | undefined) =>
    fieldErrorText(formatMessage, error, 'newPartner.')

  // `form` is stable, so the effect re-runs only when a new error arrives.
  useEffect(
    function mapServerFieldErrors() {
      for (const entry of extractValidationFieldErrors(serverError) ?? []) {
        if (isServerField(entry.field)) {
          form.setError(NEW_PARTNER_SERVER_FIELDS[entry.field], {
            type: 'server',
            message: entry.message,
          })
        }
      }
    },
    [form, serverError],
  )

  // Base UI parks focus on the popup container when the dialog opens; defer one
  // frame so focus lands on the first field and the admin can type at once.
  useEffect(
    function focusNameOnOpen() {
      const raf = requestAnimationFrame(() => form.setFocus('displayName'))

      return () => cancelAnimationFrame(raf)
    },
    [form],
  )

  const submit = form.handleSubmit((values) => onSubmit(buildNewPartnerRequest(values)))

  return (
    <>
      <AppDialog.Body>
        <MutationError
          data-tst="new-partner-error"
          detail={extractValidationDetail(serverError)}
          messageId="newPartner.error"
          show={showsBanner(serverError)}
        />
        <Controller
          control={form.control}
          name="displayName"
          render={({ field, fieldState }) => (
            <TextField
              ref={field.ref}
              required
              data-tst="new-partner-display-name"
              error={errorText(fieldState.error)}
              label={formatMessage({ id: 'newPartner.displayName' })}
              maxLength={MAX_DISPLAY_NAME_LENGTH}
              name={field.name}
              value={field.value}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <TextField
              ref={field.ref}
              required
              data-tst="new-partner-email"
              error={errorText(fieldState.error)}
              helperText={formatMessage({ id: 'newPartner.emailHelper' })}
              label={formatMessage({ id: 'newPartner.email' })}
              name={field.name}
              type="email"
              value={field.value}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={form.control}
          name="couponCodes"
          render={({ field, fieldState }) => (
            <TextField
              ref={field.ref}
              required
              data-tst="new-partner-coupon-codes"
              error={errorText(fieldState.error)}
              helperText={formatMessage({ id: 'newPartner.couponCodesHelper' })}
              label={formatMessage({ id: 'newPartner.couponCodes' })}
              name={field.name}
              value={field.value}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
            />
          )}
        />
        <Controller
          control={form.control}
          name="tierId"
          render={({ field, fieldState }) => (
            <SelectField
              required
              error={errorText(fieldState.error)}
              helperText={formatMessage({ id: 'newPartner.tierHelper' })}
              label={formatMessage({ id: 'newPartner.tier' })}
              name={field.name}
              triggerRef={field.ref}
              value={field.value}
              options={tiers.map((tier) => ({
                value: String(tier.id),
                label: `${tier.name} (${formatRate(tier.commission_rate)})`,
              }))}
              onBlur={field.onBlur}
              onValueChange={field.onChange}
            />
          )}
        />
        <Controller
          control={form.control}
          name="country"
          render={({ field, fieldState }) => (
            <TextField
              ref={field.ref}
              data-tst="new-partner-country"
              error={errorText(fieldState.error)}
              helperText={formatMessage({ id: 'newPartner.countryHelper' })}
              label={formatMessage({ id: 'newPartner.country' })}
              maxLength={2}
              name={field.name}
              value={field.value}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
            />
          )}
        />
      </AppDialog.Body>
      <AppDialog.Footer>
        <Button onClick={onClose}>
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <Button
          color="primary"
          data-tst="new-partner-submit"
          disabled={submitting}
          variant="contained"
          onClick={() => void submit()}
        >
          <FormattedMessage id="newPartner.submit" />
        </Button>
      </AppDialog.Footer>
    </>
  )
}
